/**
 * INFRA MAP — Mappa interattiva cluster EDG VoIP + export PDF/JSON.
 * Task #1638. Dataset: src/data/cluster-topology.ts (sync manuale con replit.md).
 */

import { useMemo, useState, useCallback, useRef, useEffect } from 'react';
import type { jsPDF as JsPDFType } from 'jspdf';
import {
  CLUSTER_TOPOLOGY,
  computeLayout,
  findNode,
  tierColor,
  type ClusterEdge,
  type ClusterNode,
  type EdgeKind,
  type LayoutResult,
  type NodeStatus,
} from '@/data/cluster-topology';
import { Button } from '@easyflow/ui-master/components/ui/button';
import { Badge } from '@easyflow/ui-master/components/ui/badge';
import { Download, FileJson, X, ExternalLink, Network as NetworkIcon, Copy, Check } from 'lucide-react';

const SOLO_LAN_LABEL = 'Solo LAN';

function publicIpLabel(publicIp: string | null): string {
  return publicIp ?? SOLO_LAN_LABEL;
}

function isLanOnlyNode(node: ClusterNode): boolean {
  return node.exposure === 'lan-only';
}

function exposureLabel(node: ClusterNode): string {
  switch (node.exposure) {
    case 'public':
      return 'Pubblico';
    case 'management-only':
      return 'WAN solo gestione';
    case 'lan-only':
      return 'Solo LAN';
  }
}

function cardIpLabel(node: ClusterNode): string {
  const ip = publicIpLabel(node.publicIp);
  return node.exposure === 'management-only' && ip !== SOLO_LAN_LABEL ? `Mgmt · ${ip}` : ip;
}

function conciseRole(role: string): string {
  const firstClause = role.split(/[—:]/, 1)[0]?.trim() ?? role.trim();
  return firstClause.length > 29 ? `${firstClause.slice(0, 27).trimEnd()}…` : firstClause;
}

interface EdgeStyle {
  color: string;
  dash: string | undefined;
}

function edgeStyle(kind: EdgeKind): EdgeStyle {
  const entry = CLUSTER_TOPOLOGY.edgeLegend.find((e) => e.kind === kind);
  return { color: entry?.color ?? '#94a3b8', dash: entry?.dash };
}

function statusTone(status: NodeStatus): string {
  switch (status) {
    case 'leader':
      return 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 border-emerald-500/40';
    case 'replica':
      return 'bg-sky-500/15 text-sky-700 dark:text-sky-300 border-sky-500/40';
    case 'ha':
      return 'bg-violet-500/15 text-violet-700 dark:text-violet-300 border-violet-500/40';
    case 'single':
    default:
      return 'bg-amber-500/15 text-amber-700 dark:text-amber-300 border-amber-500/40';
  }
}

interface EdgePath {
  edge: ClusterEdge;
  d: string;
  x1: number;
  y1: number;
  x2: number;
  y2: number;
}

function buildEdgePaths(layout: LayoutResult): EdgePath[] {
  const result: EdgePath[] = [];
  for (const edge of CLUSTER_TOPOLOGY.edges) {
    const a = layout.nodePositions[edge.from];
    const b = layout.nodePositions[edge.to];
    if (!a || !b) continue;
    const ax = a.x + a.w / 2;
    const ay = a.y + a.h / 2;
    const bx = b.x + b.w / 2;
    const by = b.y + b.h / 2;
    const mx = (ax + bx) / 2;
    const dx = Math.abs(bx - ax);
    const curve = Math.min(80, dx / 3);
    const d = `M ${ax} ${ay} C ${mx} ${ay - curve}, ${mx} ${by - curve}, ${bx} ${by}`;
    result.push({ edge, d, x1: ax, y1: ay, x2: bx, y2: by });
  }
  return result;
}

export default function InfraMap() {
  const layout = useMemo(() => computeLayout(), []);
  const edges = useMemo(() => buildEdgePaths(layout), [layout]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [hoverEdgeId, setHoverEdgeId] = useState<string | null>(null);
  const [hoveredNodeId, setHoveredNodeId] = useState<string | null>(null);
  const [copiedPath, setCopiedPath] = useState<string | null>(null);
  const [pdfLoading, setPdfLoading] = useState(false);
  const selectedNode: ClusterNode | undefined = selectedId ? findNode(selectedId) : undefined;
  const hoveredNode: ClusterNode | undefined = hoveredNodeId ? findNode(hoveredNodeId) : undefined;
  const webUpstreams = CLUSTER_TOPOLOGY.edges
    .filter((edge) => edge.from === 'lb1' && edge.kind === 'app-traffic')
    .map((edge) => findNode(edge.to)?.label)
    .filter((label): label is string => Boolean(label));
  const sipTargets = CLUSTER_TOPOLOGY.edges
    .filter((edge) => edge.from === 'kam1' && edge.kind === 'sip')
    .map((edge) => findNode(edge.to)?.label)
    .filter((label): label is string => Boolean(label));
  const backendWorkers = CLUSTER_TOPOLOGY.backendRuntime.nodeIds.length
    * CLUSTER_TOPOLOGY.backendRuntime.workersPerNode;
  const backendNodeLabels = CLUSTER_TOPOLOGY.backendRuntime.nodeIds
    .map((id) => findNode(id)?.label)
    .filter((label): label is string => Boolean(label));
  const lanOnlyNodes = CLUSTER_TOPOLOGY.nodes.filter(isLanOnlyNode);

  const mapWrapperRef = useRef<HTMLDivElement | null>(null);
  const mapScrollRef = useRef<HTMLDivElement | null>(null);
  const [wrapperSize, setWrapperSize] = useState<{ w: number; h: number; scrollX: number }>({ w: 0, h: 0, scrollX: 0 });

  useEffect(() => {
    const el = mapWrapperRef.current;
    if (!el) return;
    const update = (): void => {
      setWrapperSize({ w: el.clientWidth, h: el.clientHeight, scrollX: mapScrollRef.current?.scrollLeft ?? 0 });
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  useEffect(() => {
    if (!hoveredNodeId) return;
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') setHoveredNodeId(null);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [hoveredNodeId]);

  const TOOLTIP_WIDTH = Math.min(320, Math.max(180, wrapperSize.w - 16));
  const TOOLTIP_GAP = 10;
  const tooltipPos = useMemo((): { left: number; top: number } | null => {
    if (!hoveredNode) return null;
    const p = layout.nodePositions[hoveredNode.id];
    if (!p || wrapperSize.w === 0) return null;
    const scale = Math.max(wrapperSize.w, 900) / layout.width;
    const cardLeftPx = p.x * scale - wrapperSize.scrollX;
    const cardRightPx = (p.x + p.w) * scale - wrapperSize.scrollX;
    const cardTopPx = p.y * scale;
    const cardBottomPx = (p.y + p.h) * scale;
    let left = cardRightPx + TOOLTIP_GAP;
    if (left + TOOLTIP_WIDTH > wrapperSize.w - 4) {
      left = cardLeftPx - TOOLTIP_GAP - TOOLTIP_WIDTH;
    }
    left = Math.max(4, Math.min(left, wrapperSize.w - TOOLTIP_WIDTH - 4));
    const wrapperHeight = wrapperSize.h || layout.height * scale;
    const estHeight = 280;
    let top = cardTopPx;
    if (top + estHeight > wrapperHeight - 4) {
      top = Math.max(4, cardBottomPx - estHeight);
    }
    if (top < 4) top = 4;
    return { left, top };
  }, [hoveredNode, layout, wrapperSize]);

  const copyPath = useCallback((path: string) => {
    void navigator.clipboard.writeText(path).then(() => {
      setCopiedPath(path);
      window.setTimeout(() => setCopiedPath((c) => (c === path ? null : c)), 1500);
    });
  }, []);

  const downloadJson = useCallback(() => {
    const blob = new Blob([JSON.stringify(CLUSTER_TOPOLOGY, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `edgvoip-cluster-topology-${CLUSTER_TOPOLOGY.version}.json`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  }, []);

  const downloadPdf = useCallback(async () => {
    setPdfLoading(true);
    try {
      const mod = await import('jspdf');
      generatePdf(mod.jsPDF, layout, edges);
    } finally {
      setPdfLoading(false);
    }
  }, [layout, edges]);

  return (
      <div className="min-w-0 space-y-5 p-4 sm:p-6">
      <header className="flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0">
          <div className="flex items-center gap-3">
            <NetworkIcon className="h-6 w-6 shrink-0 text-purple-500" />
            <h1 className="text-xl font-semibold text-foreground dark:text-white sm:text-2xl">Mappa Infrastruttura</h1>
          </div>
          <p className="mt-1 break-words text-sm text-muted-foreground dark:text-muted-foreground/70">
            Cluster prod EDG VoIP — {CLUSTER_TOPOLOGY.nodes.length} VPS Seeweb su {CLUSTER_TOPOLOGY.tiers.length} tier. Allineato a <code className="text-xs">docs/runbooks/cluster-topology.md</code> · versione dataset {CLUSTER_TOPOLOGY.version}.
          </p>
        </div>
        <div className="flex w-full flex-wrap gap-2 sm:w-auto">
          <Button className="flex-1 sm:flex-none" variant="outline" onClick={downloadJson}>
            <FileJson className="h-4 w-4 mr-2" /> Scarica JSON
          </Button>
          <Button className="flex-1 sm:flex-none" onClick={() => { void downloadPdf(); }} disabled={pdfLoading}>
            <Download className="h-4 w-4 mr-2" /> {pdfLoading ? 'Genero PDF…' : 'Scarica PDF'}
          </Button>
        </div>
      </header>

      <section aria-label="Panoramica produzione" className="grid min-w-0 gap-3 rounded-xl border border-border bg-slate-50 p-4 dark:border-white/10 dark:bg-slate-900/40 sm:grid-cols-2 xl:grid-cols-4">
        <div className="min-w-0">
          <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Routing web</div>
          <div className="mt-1 break-words text-sm font-semibold">LB-1 → {webUpstreams.length ? webUpstreams.join(' / ') : 'nessun upstream'}</div>
          <p className="mt-1 text-xs text-muted-foreground">Ingress web pubblico e proxy verso le origini interne.</p>
        </div>
        <div className="min-w-0">
          <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Backend</div>
          <div className="mt-1 text-sm font-semibold">{backendWorkers || '—'} worker totali</div>
          <p className="mt-1 break-words text-xs text-muted-foreground">
            {backendNodeLabels.length ? `${backendNodeLabels.join(' / ')} · ${CLUSTER_TOPOLOGY.backendRuntime.workersPerNode} per nodo` : 'Capacità dichiarata nel runtime del dataset.'}
          </p>
        </div>
        <div className="min-w-0">
          <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Traffico SIP</div>
          <div className="mt-1 break-words text-sm font-semibold">KAM-1 → {sipTargets.length ? sipTargets.join(' / ') : 'nessuna destinazione'}</div>
          <p className="mt-1 text-xs text-muted-foreground">Dispatcher SIP del dataset; i nodi destinatari non espongono ingressi SIP pubblici.</p>
        </div>
        <div className="min-w-0">
          <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Servizi LAN-only</div>
          <div className="mt-1 break-words text-sm font-semibold">{lanOnlyNodes.length ? lanOnlyNodes.map((node) => node.label).join(' · ') : 'Nessun nodo'}</div>
          <p className="mt-1 text-xs text-muted-foreground">Servizi interni senza IP pubblico; esclusa l’esposizione WAN di sola gestione.</p>
        </div>
      </section>
      <p className="-mt-3 text-xs text-muted-foreground">
        Inventario statico: gli stati LEADER/REPLICA sono ruoli dichiarati; questa pagina non interroga health check o metriche live.
      </p>

      <div className="grid min-w-0 grid-cols-1 gap-4 xl:grid-cols-[minmax(0,1fr)_360px]">
        <div className="min-w-0 rounded-xl border border-border bg-white p-3 dark:border-white/10 dark:bg-slate-900/60">
          <div ref={mapWrapperRef} className="relative min-w-0">
            <div
              ref={mapScrollRef}
              className="min-w-0 overflow-x-auto"
              onScroll={(event) => {
                const scrollX = event.currentTarget.scrollLeft;
                setWrapperSize((current) => ({ ...current, scrollX }));
              }}
            >
          <svg
            viewBox={`0 0 ${layout.width} ${layout.height}`}
            className="h-auto min-w-[900px] w-full"
            role="img"
            aria-label="Mappa cluster EDG VoIP"
          >
            <defs>
              <marker id="arrow-end" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="6" markerHeight="6" orient="auto-start-reverse">
                <path d="M 0 0 L 10 5 L 0 10 z" fill="#64748b" />
              </marker>
            </defs>

            {layout.tierBands.map((band) => (
              <g key={band.id}>
                <rect
                  x={band.x}
                  y={band.y}
                  width={band.w}
                  height={band.h}
                  rx={16}
                  fill={tierColor(band.id)}
                  fillOpacity={0.05}
                  stroke={tierColor(band.id)}
                  strokeOpacity={0.35}
                  strokeWidth={1}
                />
                <text
                  x={band.x + 12}
                  y={band.y + 22}
                  fontSize={14}
                  fontWeight={600}
                  fill={tierColor(band.id)}
                >
                  {band.label.toUpperCase()}
                </text>
              </g>
            ))}

            {edges.map((ep) => {
              const style = edgeStyle(ep.edge.kind);
              const hover = hoverEdgeId === ep.edge.id;
              return (
                <g key={ep.edge.id} onMouseEnter={() => setHoverEdgeId(ep.edge.id)} onMouseLeave={() => setHoverEdgeId(null)}>
                  <path
                    d={ep.d}
                    fill="none"
                    stroke={style.color}
                    strokeWidth={hover ? 3 : 1.5}
                    strokeOpacity={hover ? 1 : 0.7}
                    strokeDasharray={style.dash}
                  />
                  {hover && (
                    <g>
                      <rect
                        x={(ep.x1 + ep.x2) / 2 - 110}
                        y={(ep.y1 + ep.y2) / 2 - 14}
                        width={220}
                        height={28}
                        rx={6}
                        fill="#0f172a"
                        fillOpacity={0.92}
                      />
                      <text
                        x={(ep.x1 + ep.x2) / 2}
                        y={(ep.y1 + ep.y2) / 2 + 4}
                        fontSize={11}
                        fill="#ffffff"
                        textAnchor="middle"
                      >
                        {ep.edge.label}
                      </text>
                    </g>
                  )}
                </g>
              );
            })}

            {CLUSTER_TOPOLOGY.nodes.map((node) => {
              const p = layout.nodePositions[node.id];
              if (!p) return null;
              const isSelected = selectedId === node.id;
              const isHovered = hoveredNodeId === node.id;
              const color = tierColor(node.tier);
              return (
                <g
                  key={node.id}
                  transform={`translate(${p.x},${p.y})`}
                  className="cursor-pointer"
                  role="button"
                  aria-label={`Nodo ${node.label}`}
                  aria-describedby={isHovered ? `infra-tooltip-${node.id}` : undefined}
                  tabIndex={0}
                  onClick={() => setSelectedId(node.id)}
                  onMouseEnter={() => setHoveredNodeId(node.id)}
                  onMouseLeave={() => setHoveredNodeId((cur) => (cur === node.id ? null : cur))}
                  onFocus={() => setHoveredNodeId(node.id)}
                  onBlur={() => setHoveredNodeId((cur) => (cur === node.id ? null : cur))}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      setSelectedId(node.id);
                    }
                  }}
                >
                  <rect
                    x={0}
                    y={0}
                    width={p.w}
                    height={p.h}
                    rx={12}
                    fill="#ffffff"
                    fillOpacity={1}
                    stroke={isSelected ? '#a855f7' : color}
                    strokeWidth={isSelected ? 3 : 2}
                    className="drop-shadow-sm"
                  />
                  <rect x={0} y={0} width={p.w} height={28} rx={12} fill={color} fillOpacity={0.15} />
                  <rect x={0} y={14} width={p.w} height={14} fill={color} fillOpacity={0.15} />
                  <text x={10} y={18} fontSize={11} fontWeight={700} fill={color}>
                    {node.label}
                  </text>
                  <text x={p.w - 10} y={18} fontSize={8} fontWeight={600} fill="#475569" textAnchor="end">
                    {node.status.toUpperCase()}
                  </text>
                  <text x={10} y={42} fontSize={9} fill="#0f172a">
                    {cardIpLabel(node)}
                  </text>
                  <text x={10} y={56} fontSize={8} fill="#64748b">
                    LAN {node.lanIp ?? '—'}
                  </text>
                  <text x={10} y={77} fontSize={8} fill="#475569">
                    {conciseRole(node.role)}
                  </text>
                </g>
              );
            })}
          </svg>
            </div>

          {hoveredNode && tooltipPos && (
            <div
              id={`infra-tooltip-${hoveredNode.id}`}
              role="tooltip"
              className="pointer-events-none absolute z-20 max-h-[min(480px,80vh)] overflow-auto rounded-xl border border-border bg-white p-3 text-foreground shadow-lg dark:border-white/10 dark:bg-slate-900/95 dark:text-slate-100"
              style={{ left: tooltipPos.left, top: tooltipPos.top, width: TOOLTIP_WIDTH, maxWidth: 'calc(100% - 8px)' }}
            >
              <div className="flex items-start justify-between gap-2">
                <div>
                  <div className="text-[10px] uppercase tracking-wide text-muted-foreground dark:text-muted-foreground/70">
                    {CLUSTER_TOPOLOGY.tiers.find((t) => t.id === hoveredNode.tier)?.label}
                  </div>
                  <div className="text-sm font-semibold" style={{ color: tierColor(hoveredNode.tier) }}>
                    {hoveredNode.label}
                  </div>
                </div>
                <span
                  className={`inline-block px-2 py-0.5 rounded text-[10px] border ${statusTone(hoveredNode.status)}`}
                >
                  {hoveredNode.status.toUpperCase()}
                </span>
              </div>

                <div className="grid grid-cols-2 gap-2 text-[11px]">
                <div>
                    <div className="text-muted-foreground dark:text-muted-foreground/70">IP / esposizione</div>
                  <div className="break-all font-mono">{publicIpLabel(hoveredNode.publicIp)}</div>
                    <div className="break-words text-[10px]">{exposureLabel(hoveredNode)}</div>
                </div>
                <div>
                  <div className="text-muted-foreground dark:text-muted-foreground/70">IP LAN</div>
                  <div className="break-all font-mono">{hoveredNode.lanIp ?? '—'}</div>
                </div>
              </div>

              <div>
                <div className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground dark:text-muted-foreground/70 mb-0.5">
                  Role
                </div>
                  <p className="break-words text-xs leading-snug">{hoveredNode.role}</p>
              </div>

              <div>
                <div className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground dark:text-muted-foreground/70 mb-0.5">
                  Servizi
                </div>
                <ul className="text-[11px] space-y-0.5">
                  {hoveredNode.services.map((s) => (
                      <li key={s} className="flex min-w-0 items-start gap-1.5">
                      <span className="text-purple-500">•</span>
                      <span className="min-w-0 break-words">{s}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="text-[10px] flex items-center gap-1.5 pt-1 border-t border-border dark:border-white/10">
                <span className="text-muted-foreground dark:text-muted-foreground/70">SSH:</span>
                <code className="break-all font-mono">{hoveredNode.sshSecret}</code>
              </div>
            </div>
          )}
          </div>

          <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4 text-xs">
            <div>
              <div className="font-semibold text-foreground dark:text-foreground mb-2">Tipi di freccia</div>
              <ul className="space-y-1">
                {CLUSTER_TOPOLOGY.edgeLegend.map((l) => (
                  <li key={l.kind} className="flex items-center gap-2">
                    <svg width="28" height="8">
                      <line x1="0" y1="4" x2="28" y2="4" stroke={l.color} strokeWidth="2" strokeDasharray={l.dash} />
                    </svg>
                <span className="min-w-0 break-words text-muted-foreground dark:text-muted-foreground/70">{l.label}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <div className="font-semibold text-foreground dark:text-foreground mb-2">Stato nodi</div>
              <ul className="grid grid-cols-2 gap-1">
                {CLUSTER_TOPOLOGY.statusLegend.map((s) => (
                  <li key={s.status} className="flex items-center gap-2">
                    <span className={`inline-block px-2 py-0.5 rounded text-[10px] border ${statusTone(s.status)}`}>{s.status.toUpperCase()}</span>
                    <span className="min-w-0 break-words text-muted-foreground dark:text-muted-foreground/70">{s.label}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>

        <aside className="min-w-0 max-h-[760px] overflow-auto rounded-xl border border-border bg-white p-4 dark:border-white/10 dark:bg-slate-900/60">
          {selectedNode ? (
            <div className="space-y-3">
              <div className="flex items-start justify-between">
                <div>
                  <div className="text-xs text-muted-foreground uppercase">{CLUSTER_TOPOLOGY.tiers.find((t) => t.id === selectedNode.tier)?.label}</div>
                  <div className="text-xl font-semibold" style={{ color: tierColor(selectedNode.tier) }}>{selectedNode.label}</div>
                </div>
                <Button variant="ghost" size="sm" onClick={() => setSelectedId(null)}>
                  <X className="h-4 w-4" />
                </Button>
              </div>
              <Badge className={statusTone(selectedNode.status)}>{selectedNode.status.toUpperCase()}</Badge>
              <p className="break-words text-sm text-foreground dark:text-muted-foreground/60">{selectedNode.role}</p>

              <div className="grid grid-cols-2 gap-2 text-xs">
                <div>
                    <div className="text-muted-foreground">IP / esposizione</div>
                    <div className="break-all font-mono">{publicIpLabel(selectedNode.publicIp)}</div>
                    <div className="break-words">{exposureLabel(selectedNode)}</div>
                </div>
                <div>
                  <div className="text-muted-foreground">IP LAN</div>
                    <div className="break-all font-mono">{selectedNode.lanIp ?? '—'}</div>
                </div>
                <div className="col-span-2">
                  <div className="text-muted-foreground">Env secret SSH</div>
                  <div className="break-all font-mono">{selectedNode.sshSecret}</div>
                </div>
              </div>

              <div>
                <div className="text-xs font-semibold text-muted-foreground dark:text-muted-foreground/60 mb-1">Servizi</div>
                <ul className="text-xs space-y-1">
                  {selectedNode.services.map((s) => (
                    <li key={s} className="flex items-start gap-2">
                      <span className="text-purple-500">•</span>
                      <span className="break-words text-foreground dark:text-muted-foreground/60">{s}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {selectedNode.notes && (
                <div className="break-words rounded border border-amber-500/30 bg-amber-500/10 p-2 text-xs text-amber-800 dark:text-amber-200">
                  {selectedNode.notes}
                </div>
              )}

              <div>
                <div className="text-xs font-semibold text-muted-foreground dark:text-muted-foreground/60 mb-1">Runbook</div>
                <ul className="text-xs space-y-1">
                  {selectedNode.runbooks.map((r) => {
                    const path = `docs/runbooks/${r}`;
                    const copied = copiedPath === path;
                    return (
                      <li key={r}>
                        <button
                          type="button"
                          onClick={() => copyPath(path)}
                          className="flex min-w-0 items-start gap-1 text-left text-foreground dark:text-muted-foreground/60 hover:text-purple-600 dark:hover:text-purple-400 underline-offset-2 hover:underline"
                          title="Copia path negli appunti"
                        >
                          {copied ? <Check className="h-3 w-3 text-emerald-500" /> : <Copy className="h-3 w-3 text-muted-foreground/70" />}
                           <code className="break-all">{path}</code>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </div>
            </div>
          ) : (
            <div className="text-sm text-muted-foreground dark:text-muted-foreground/70">
              Clicca su un nodo della mappa per vedere ruolo, IP, servizi e runbook associati.
            </div>
          )}
        </aside>
      </div>

      <section className="grid min-w-0 grid-cols-1 gap-4 lg:grid-cols-2">
        <div className="min-w-0 rounded-xl border border-border bg-white p-4 dark:border-white/10 dark:bg-slate-900/60">
          <h2 className="text-lg font-semibold mb-2">Stack tecnologico</h2>
          <ul className="text-sm space-y-1 text-foreground dark:text-muted-foreground/60">
            {CLUSTER_TOPOLOGY.stack.map((s) => (
              <li key={s} className="flex min-w-0 items-start gap-2">
                <span className="text-purple-500">›</span>
                <span className="break-words">{s}</span>
              </li>
            ))}
          </ul>
          <h3 className="text-sm font-semibold mt-4 mb-1">Endpoint pubblici</h3>
          <ul className="text-xs space-y-1">
            {CLUSTER_TOPOLOGY.publicEndpoints.map((e) => (
              <li key={e.label} className="grid min-w-0 grid-cols-1 gap-x-3 border-b border-border/60 py-1 font-mono sm:grid-cols-[minmax(0,1fr)_minmax(0,1.2fr)] dark:border-white/10">
                <span className="break-words text-muted-foreground">{e.label}</span>
                <span className="break-all text-foreground dark:text-foreground">{e.value}</span>
              </li>
            ))}
          </ul>
        </div>

        <div className="min-w-0 rounded-xl border border-border bg-white p-4 dark:border-white/10 dark:bg-slate-900/60">
          <h2 className="text-lg font-semibold mb-2">Vincoli architetturali</h2>
          <ul className="text-sm space-y-2 text-foreground dark:text-muted-foreground/60">
            {CLUSTER_TOPOLOGY.constraints.map((c) => (
              <li key={c.title}>
                <div className="font-semibold text-foreground dark:text-white">{c.title}</div>
                <div className="break-words text-xs text-muted-foreground dark:text-muted-foreground/70">{c.detail}</div>
              </li>
            ))}
          </ul>
        </div>
      </section>

      <section className="min-w-0 rounded-xl border border-border bg-white p-4 dark:border-white/10 dark:bg-slate-900/60">
        <h2 className="text-lg font-semibold mb-2">Runbook index</h2>
        <div className="grid min-w-0 grid-cols-1 gap-2 text-xs md:grid-cols-3">
          {CLUSTER_TOPOLOGY.runbookIndex.map((r) => {
            const copied = copiedPath === r.path;
            return (
              <button
                key={r.path}
                type="button"
                onClick={() => copyPath(r.path)}
                className="flex items-start gap-2 text-left p-2 rounded hover:bg-purple-50 dark:hover:bg-purple-500/10 transition"
                title="Copia path negli appunti"
              >
                {copied ? <Check className="h-3 w-3 mt-1 text-emerald-500 shrink-0" /> : <ExternalLink className="h-3 w-3 mt-1 text-muted-foreground/70 shrink-0" />}
                <div className="min-w-0">
                  <div className="font-semibold text-foreground dark:text-foreground">{r.title}</div>
                  <code className="break-all text-muted-foreground">{r.path}</code>
                </div>
              </button>
            );
          })}
        </div>
      </section>
    </div>
  );
}

/* ---------------- PDF generation ---------------- */

function hexToRgb(hex: string): [number, number, number] {
  const h = hex.replace('#', '');
  const v = parseInt(h.length === 3 ? h.split('').map((c) => c + c).join('') : h, 16);
  return [(v >> 16) & 255, (v >> 8) & 255, v & 255];
}

interface PdfLayout {
  pdfW: number;
  pdfH: number;
  offX: number;
  offY: number;
  scale: number;
}

type JsPDFCtor = new (opts: { orientation: 'landscape' | 'portrait'; unit: 'mm'; format: 'a3' }) => JsPDFType;

function generatePdf(JsPdf: JsPDFCtor, layout: LayoutResult, edges: EdgePath[]): void {
  const doc = new JsPdf({ orientation: 'landscape', unit: 'mm', format: 'a3' });
  const pdfW = doc.internal.pageSize.getWidth();
  const pdfH = doc.internal.pageSize.getHeight();

  doc.setFillColor(248, 250, 252);
  doc.rect(0, 0, pdfW, pdfH, 'F');

  doc.setTextColor(15, 23, 42);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(18);
  doc.text('EDG VoIP — Mappa Infrastruttura cluster prod', 12, 14);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(100, 116, 139);
  const now = new Date();
  doc.text(
    `Generato ${now.toLocaleString('it-IT')} · dataset ${CLUSTER_TOPOLOGY.version} · ${CLUSTER_TOPOLOGY.nodes.length} nodi · inventario statico, stati non live`,
    12,
    19,
  );

  // Map area: left ~62% of page width.
  const mapBoxX = 8;
  const mapBoxY = 24;
  const mapBoxW = pdfW * 0.6 - 12;
  const mapBoxH = pdfH * 0.62;
  const scale = Math.min(mapBoxW / layout.width, mapBoxH / layout.height);
  const offX = mapBoxX + (mapBoxW - layout.width * scale) / 2;
  const offY = mapBoxY + (mapBoxH - layout.height * scale) / 2;
  const ctx: PdfLayout = { pdfW, pdfH, offX, offY, scale };

  drawMapOnPdf(doc, layout, edges, ctx);
  drawLegendOnPdf(doc, mapBoxX, mapBoxY + mapBoxH + 4, mapBoxW);
  drawSidePanelOnPdf(doc, pdfW * 0.6 + 2, 24, pdfW - (pdfW * 0.6 + 2) - 8, pdfH - 24 - 8);

  // Page 2: complete node inventory. Long rows flow across pages instead of being clipped.
  doc.addPage('a3', 'landscape');
  doc.setFillColor(248, 250, 252);
  doc.rect(0, 0, pdfW, pdfH, 'F');
  doc.setTextColor(15, 23, 42);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(16);
  doc.text('Inventario completo dei nodi', 12, 14);
  drawNodesTableOnPdf(doc, 8, 22, pdfW - 16, pdfH);

  // Reference material gets dedicated pages so lengthy text remains readable.
  doc.addPage('a3', 'landscape');
  doc.setFillColor(248, 250, 252);
  doc.rect(0, 0, pdfW, pdfH, 'F');
  doc.setTextColor(15, 23, 42);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(16);
  doc.text('Stack e endpoint pubblici', 12, 14);
  drawStackAndEndpointsOnPdf(doc, 10, 24, pdfW / 2 - 18, pdfH - 32);
  doc.addPage('a3', 'landscape');
  doc.setFillColor(248, 250, 252);
  doc.rect(0, 0, pdfW, pdfH, 'F');
  doc.setTextColor(15, 23, 42);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(16);
  doc.text('Vincoli architetturali e runbook', 12, 14);
  drawConstraintsOnPdf(doc, 10, 24, pdfW / 2 - 20);
  drawRunbookIndexOnPdf(doc, pdfW / 2 + 2, 24, pdfW / 2 - 12);

  doc.save(`edgvoip-infra-map-${CLUSTER_TOPOLOGY.version}.pdf`);
}

function drawMapOnPdf(doc: JsPDFType, layout: LayoutResult, edges: EdgePath[], ctx: PdfLayout): void {
  const { offX, offY, scale } = ctx;

  // Tier bands.
  layout.tierBands.forEach((band) => {
    const [r, g, b] = hexToRgb(tierColor(band.id));
    doc.setDrawColor(r, g, b);
    doc.setFillColor(r, g, b);
    doc.setLineWidth(0.2);
    doc.setGState(doc.GState({ opacity: 0.06 }));
    doc.roundedRect(offX + band.x * scale, offY + band.y * scale, band.w * scale, band.h * scale, 3, 3, 'F');
    doc.setGState(doc.GState({ opacity: 1 }));
    doc.setFontSize(9);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(r, g, b);
    doc.text(band.label.toUpperCase(), offX + (band.x + 4) * scale, offY + (band.y + 8) * scale);
  });

  // Edges.
  edges.forEach((ep) => {
    const style = edgeStyle(ep.edge.kind);
    const [r, g, b] = hexToRgb(style.color);
    doc.setDrawColor(r, g, b);
    doc.setLineWidth(0.3);
    if (style.dash) {
      const parts = style.dash.split(' ').map(Number);
      doc.setLineDashPattern(parts, 0);
    } else {
      doc.setLineDashPattern([], 0);
    }
    // Approximate cubic bezier with sampled polyline (jsPDF lacks bezier in mm easily).
    const samples = 18;
    let prevX = ep.x1;
    let prevY = ep.y1;
    const cpx1 = (ep.x1 + ep.x2) / 2;
    const cpy1 = ep.y1 - Math.min(80, Math.abs(ep.x2 - ep.x1) / 3);
    const cpx2 = (ep.x1 + ep.x2) / 2;
    const cpy2 = ep.y2 - Math.min(80, Math.abs(ep.x2 - ep.x1) / 3);
    for (let i = 1; i <= samples; i++) {
      const t = i / samples;
      const mt = 1 - t;
      const x =
        mt * mt * mt * ep.x1 +
        3 * mt * mt * t * cpx1 +
        3 * mt * t * t * cpx2 +
        t * t * t * ep.x2;
      const y =
        mt * mt * mt * ep.y1 +
        3 * mt * mt * t * cpy1 +
        3 * mt * t * t * cpy2 +
        t * t * t * ep.y2;
      doc.line(offX + prevX * scale, offY + prevY * scale, offX + x * scale, offY + y * scale);
      prevX = x;
      prevY = y;
    }
  });
  doc.setLineDashPattern([], 0);

  // Nodes.
  CLUSTER_TOPOLOGY.nodes.forEach((node) => {
    const p = layout.nodePositions[node.id];
    if (!p) return;
    const [r, g, b] = hexToRgb(tierColor(node.tier));
    const nx = offX + p.x * scale;
    const ny = offY + p.y * scale;
    const nw = p.w * scale;
    const nh = p.h * scale;
    doc.setFillColor(255, 255, 255);
    doc.setDrawColor(r, g, b);
    doc.setLineWidth(0.4);
    doc.roundedRect(nx, ny, nw, nh, 2, 2, 'FD');
    doc.setFillColor(r, g, b);
    doc.setGState(doc.GState({ opacity: 0.15 }));
    doc.roundedRect(nx, ny, nw, nh / 3, 2, 2, 'F');
    doc.setGState(doc.GState({ opacity: 1 }));
    doc.setTextColor(r, g, b);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(7);
    doc.text(node.label, nx + 2, ny + 5);
    doc.setFontSize(5.5);
    doc.setTextColor(71, 85, 105);
    doc.text(node.status.toUpperCase(), nx + nw - 2, ny + 5, { align: 'right' });
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(5.8);
    doc.setTextColor(15, 23, 42);
    doc.text(cardIpLabel(node), nx + 2, ny + nh / 3 + 4);
    doc.setTextColor(100, 116, 139);
    doc.text(`LAN ${node.lanIp ?? '—'}`, nx + 2, ny + nh / 3 + 7);
    doc.setTextColor(71, 85, 105);
    const roleLines = doc.splitTextToSize(conciseRole(node.role), Math.max(10, nw - 4));
    doc.text(roleLines.slice(0, 1), nx + 2, ny + nh - 3);
  });
}

function drawLegendOnPdf(doc: JsPDFType, x: number, y: number, w: number): void {
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10);
  doc.setTextColor(15, 23, 42);
  doc.text('Legenda', x, y + 4);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(7.5);
  const half = w / 2 - 4;

  let cy = y + 9;
  CLUSTER_TOPOLOGY.edgeLegend.forEach((l) => {
    const [r, g, b] = hexToRgb(l.color);
    doc.setDrawColor(r, g, b);
    doc.setLineWidth(0.5);
    if (l.dash) {
      doc.setLineDashPattern(l.dash.split(' ').map(Number), 0);
    } else {
      doc.setLineDashPattern([], 0);
    }
    doc.line(x, cy, x + 10, cy);
    doc.setLineDashPattern([], 0);
    doc.setTextColor(51, 65, 85);
    doc.text(l.label, x + 12, cy + 1.2);
    cy += 4.5;
  });

  let cy2 = y + 9;
  CLUSTER_TOPOLOGY.statusLegend.forEach((s) => {
    doc.setFillColor(226, 232, 240);
    doc.roundedRect(x + half, cy2 - 2.6, 14, 3.6, 0.6, 0.6, 'F');
    doc.setTextColor(15, 23, 42);
    doc.setFontSize(6);
    doc.text(s.status.toUpperCase(), x + half + 7, cy2 + 0.2, { align: 'center' });
    doc.setFontSize(7.5);
    doc.setTextColor(51, 65, 85);
    doc.text(s.label, x + half + 16, cy2 + 1.2);
    cy2 += 4.5;
  });
}

function drawSidePanelOnPdf(doc: JsPDFType, x: number, y: number, w: number, h: number): void {
  doc.setFillColor(255, 255, 255);
  doc.setDrawColor(226, 232, 240);
  doc.setLineWidth(0.2);
  doc.roundedRect(x, y, w, h, 2, 2, 'FD');

  let cy = y + 6;
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11);
  doc.setTextColor(15, 23, 42);
  doc.text('Info principali', x + 4, cy);
  cy += 6;
  const appTargets = CLUSTER_TOPOLOGY.edges
    .filter((edge) => edge.from === 'lb1' && edge.kind === 'app-traffic')
    .map((edge) => findNode(edge.to)?.label)
    .filter((label): label is string => Boolean(label));
  const sipTargets = CLUSTER_TOPOLOGY.edges
    .filter((edge) => edge.from === 'kam1' && edge.kind === 'sip')
    .map((edge) => findNode(edge.to)?.label)
    .filter((label): label is string => Boolean(label));
  const workerCount = CLUSTER_TOPOLOGY.backendRuntime.nodeIds.length
    * CLUSTER_TOPOLOGY.backendRuntime.workersPerNode;
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(99, 102, 241);
  doc.setFontSize(7.5);
  doc.setTextColor(51, 65, 85);
  const highlights = [
    `Web: LB-1 → ${appTargets.join(' / ') || 'nessun upstream'}`,
    `Backend: ${workerCount || '—'} worker complessivi`,
    `SIP: KAM-1 → ${sipTargets.join(' / ') || 'nessuna destinazione'}`,
    `LAN-only: ${CLUSTER_TOPOLOGY.nodes.filter(isLanOnlyNode).map((node) => node.label).join(', ') || 'nessun nodo'}`,
  ];
  highlights.forEach((item) => {
    const lines = doc.splitTextToSize(item, w - 8);
    doc.text(lines, x + 4, cy);
    cy += lines.length * 3.2 + 1;
  });
  cy += 1;
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(99, 102, 241);
  doc.setFontSize(8);
  doc.text('Endpoint pubblici', x + 4, cy);
  cy += 4;
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(51, 65, 85);
  doc.setFontSize(6.6);
  CLUSTER_TOPOLOGY.publicEndpoints.forEach((endpoint) => {
    const lines = doc.splitTextToSize(`${endpoint.label}: ${endpoint.value}`, w - 8);
    const lineHeight = 2.8;
    if (cy + lines.length * lineHeight > y + h - 3) return;
    doc.text(lines, x + 4, cy);
    cy += lines.length * lineHeight + 0.8;
  });
}

function drawNodesTableOnPdf(doc: JsPDFType, x: number, y: number, w: number, pageHeight: number): void {
  const fixedWidths = [19, 25, 18, 30, 25, 27, 48];
  const cols = [
    { label: 'Nodo', w: fixedWidths[0] ?? 19 },
    { label: 'Tier', w: fixedWidths[1] ?? 25 },
    { label: 'Stato', w: fixedWidths[2] ?? 18 },
    { label: 'IP WAN', w: fixedWidths[3] ?? 30 },
    { label: 'IP LAN', w: fixedWidths[4] ?? 25 },
    { label: 'Esposizione', w: fixedWidths[5] ?? 27 },
    { label: 'Env SSH', w: fixedWidths[6] ?? 48 },
    { label: 'Ruolo · servizi · note', w: w - fixedWidths.reduce((sum, value) => sum + value, 0) },
  ];
  const headerHeight = 7;
  const drawHeader = (top: number): void => {
    let cursorX = x;
    doc.setFillColor(91, 78, 166);
    doc.rect(x, top, w, headerHeight, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(7);
    cols.forEach((col) => {
      doc.text(col.label, cursorX + 1, top + 4.6);
      cursorX += col.w;
    });
  };
  let rowY = y;
  drawHeader(rowY);
  rowY += headerHeight;
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(6.4);
  CLUSTER_TOPOLOGY.nodes.forEach((node, idx) => {
    const tier = CLUSTER_TOPOLOGY.tiers.find((entry) => entry.id === node.tier)?.label ?? node.tier;
    const fullRole = [node.role, ...(node.notes ? [node.notes] : [])].join(' · ');
    const fullServices = node.services.join(' · ');
    const cells = [
      node.label,
      tier,
      node.status.toUpperCase(),
      publicIpLabel(node.publicIp),
      node.lanIp ?? '—',
      exposureLabel(node),
      node.sshSecret,
      `${fullRole} · Servizi: ${fullServices}`,
    ];
    const linesByCell = cells.map((value, cellIndex) => {
      const col = cols[cellIndex];
      return col ? doc.splitTextToSize(value, Math.max(8, col.w - 2)) as string[] : [value];
    });
    const lineCount = Math.max(1, ...linesByCell.map((lines) => lines.length));
    const rowHeight = Math.max(6, lineCount * 2.75 + 2);
    if (rowY + rowHeight > pageHeight - 8) {
      doc.addPage('a3', 'landscape');
      doc.setFillColor(248, 250, 252);
      doc.rect(0, 0, doc.internal.pageSize.getWidth(), pageHeight, 'F');
      doc.setTextColor(15, 23, 42);
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(12);
      doc.text('Inventario completo dei nodi · continua', 12, 14);
      rowY = 20;
      drawHeader(rowY);
      rowY += headerHeight;
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(6.4);
    }
    if (idx % 2 === 0) {
      doc.setFillColor(241, 245, 249);
      doc.rect(x, rowY, w, rowHeight, 'F');
    }
    let cursorX = x;
    linesByCell.forEach((lines, cellIndex) => {
      const col = cols[cellIndex];
      if (!col) return;
      doc.setTextColor(cellIndex === 6 ? 51 : 15, cellIndex === 6 ? 65 : 23, cellIndex === 6 ? 85 : 42);
      doc.text(lines, cursorX + 1, rowY + 3.2);
      cursorX += col.w;
    });
    doc.setDrawColor(226, 232, 240);
    doc.setLineWidth(0.15);
    doc.line(x, rowY + rowHeight, x + w, rowY + rowHeight);
    rowY += rowHeight;
  });
}

function drawStackAndEndpointsOnPdf(doc: JsPDFType, x: number, y: number, w: number, h: number): void {
  let cy = y;
  const bottom = y + h;
  const drawSectionTitle = (title: string): void => {
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.setTextColor(91, 78, 166);
    doc.text(title, x, cy);
    cy += 6;
  };
  drawSectionTitle('Stack tecnologico');
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);
  doc.setTextColor(51, 65, 85);
  CLUSTER_TOPOLOGY.stack.forEach((entry) => {
    const lines = doc.splitTextToSize(`• ${entry}`, w);
    if (cy + lines.length * 3.6 > bottom) return;
    doc.text(lines, x, cy);
    cy += lines.length * 3.6 + 1.4;
  });
  cy += 3;
  drawSectionTitle('Endpoint pubblici');
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(7.5);
  CLUSTER_TOPOLOGY.publicEndpoints.forEach((endpoint) => {
    const lines = doc.splitTextToSize(`${endpoint.label}: ${endpoint.value}`, w);
    const blockHeight = lines.length * 3.4 + 1.2;
    if (cy + blockHeight > bottom) return;
    doc.text(lines, x, cy);
    cy += blockHeight;
  });
}

function drawConstraintsOnPdf(doc: JsPDFType, x: number, y: number, w: number): void {
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11);
  doc.setTextColor(15, 23, 42);
  doc.text('Vincoli architetturali', x, y);
  let cy = y + 5;
  doc.setFontSize(8);
  CLUSTER_TOPOLOGY.constraints.forEach((c) => {
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(99, 102, 241);
    const titleLines = doc.splitTextToSize(c.title, w);
    doc.text(titleLines, x, cy);
    cy += titleLines.length * 3.5;
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(51, 65, 85);
    const lines = doc.splitTextToSize(c.detail, w);
    doc.text(lines, x, cy);
    cy += lines.length * 3.2 + 1.5;
  });
}

function drawRunbookIndexOnPdf(doc: JsPDFType, x: number, y: number, w: number): void {
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11);
  doc.setTextColor(15, 23, 42);
  doc.text('Runbook index', x, y);
  let cy = y + 5;
  doc.setFontSize(8);
  CLUSTER_TOPOLOGY.runbookIndex.forEach((r) => {
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(15, 23, 42);
    const titleLines = doc.splitTextToSize(r.title, w);
    doc.text(titleLines, x, cy);
    cy += titleLines.length * 3.3;
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(100, 116, 139);
    const pathLines = doc.splitTextToSize(r.path, w);
    doc.text(pathLines, x, cy);
    cy += pathLines.length * 4.2;
  });
}
